import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Send, Loader2, Mail, Copy, Pencil, Zap } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { contactName, greetingFor } from "@/lib/salutation";

const PLACEHOLDER_HINT = "{anrede}, {name}, {termin}, {datum}, {uhrzeit}, {ort}, {leistung}";

export default function Templates() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sending, setSending] = useState(null); // template object

  const load = async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([api.get("/email-templates"), api.get("/customers")]);
      setItems(t.data); setCustomers(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Vorlage löschen?")) return;
    try { await api.delete(`/email-templates/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="crm-templates">
      <div className="mb-5 md:mb-8 flex items-start justify-between gap-3 md:gap-4 flex-wrap">
        <div>
          <div className="hidden md:block text-xs font-medium text-[#6B7F3E] uppercase tracking-wider mb-2">CRM</div>
          <h1 className="hidden md:block font-display text-3xl font-semibold text-[#1A201A] tracking-tight">E-Mail-Vorlagen</h1>
          <p className="text-sm text-[#5C6B5D] mt-1">
            Personalisierbare Antwortentwürfe. Platzhalter: {PLACEHOLDER_HINT}.
          </p>
        </div>
        <Button className="bg-[#6B7F3E] hover:bg-[#5A6B34]" onClick={() => setCreating(true)} data-testid="template-new-btn">
          <Plus className="w-4 h-4 mr-1" /> Neu
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading && <div className="text-sm text-[#5C6B5D] col-span-full">Lädt…</div>}
        {!loading && items.length === 0 && (
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-10 text-center text-sm text-[#5C6B5D] col-span-full">
            Noch keine Vorlagen angelegt.
          </div>
        )}
        {items.map((t) => (
          <div key={t.id} data-testid={`template-card-${t.id}`} className="bg-white border border-[#E5E7EB] rounded-xl p-5 hover:border-[#6B7F3E]/50 flex flex-col">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#6B7F3E] flex items-center gap-1.5">
                  {t.category}
                  {t.system && (
                    <span className="inline-flex items-center gap-0.5 normal-case tracking-normal rounded-full bg-[#E8ECE1] text-[#2C3E2D] px-1.5 py-0.5" data-testid={`template-system-${t.id}`}>
                      <Zap className="w-3 h-3" /> Automatisch
                    </span>
                  )}
                </div>
                <div className="font-medium text-[#1A201A]">{t.name}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setEditing(t)} data-testid={`template-edit-${t.id}`} className="text-[#5C6B5D] hover:text-[#2C3E2D]" aria-label="Vorlage bearbeiten">
                  <Pencil className="w-4 h-4" />
                </button>
                {!t.system && (
                  <button onClick={() => remove(t.id)} data-testid={`template-delete-${t.id}`} className="text-[#B85C38] hover:text-[#7A2E14]" aria-label="Vorlage löschen">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            {t.usage && <div className="text-xs text-[#5C6B5D] bg-[#F5F6F0] rounded-md px-2.5 py-1.5 mb-3">{t.usage}</div>}
            <div className="text-xs text-[#5C6B5D] mb-1">Betreff:</div>
            <div className="text-sm text-[#2C3E2D] mb-3">{t.subject}</div>
            <div className="text-xs text-[#5C6B5D] mb-1">Text:</div>
            <div className="text-sm text-[#4A5D4E] whitespace-pre-wrap flex-1 mb-4 line-clamp-6">{t.body}</div>
            <Button
              size="sm"
              className="bg-[#6B7F3E] hover:bg-[#5A6B34] text-white self-end"
              onClick={() => setSending(t)}
              data-testid={`template-use-${t.id}`}
            >
              <Send className="w-3.5 h-3.5 mr-1" /> Verwenden
            </Button>
          </div>
        ))}
      </div>

      <CreateDialog open={creating} onOpenChange={setCreating} onSaved={load} />
      <CreateDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} onSaved={load} template={editing} />
      <SendDialog open={!!sending} template={sending} customers={customers} onOpenChange={(o) => !o && setSending(null)} />
    </div>
  );
}

// template gesetzt → Bearbeiten-Modus
function CreateDialog({ open, onOpenChange, onSaved, template = null }) {
  const [form, setForm] = useState({ name: "", subject: "", body: "", category: "Allgemein" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(template
      ? { name: template.name, subject: template.subject, body: template.body, category: template.category || "Allgemein" }
      : { name: "", subject: "", body: "", category: "Allgemein" });
  }, [open, template]);
  const save = async () => {
    if (!form.name || !form.subject || !form.body) { toast.error("Alle Pflichtfelder ausfüllen"); return; }
    setSaving(true);
    try {
      if (template) await api.put(`/email-templates/${template.id}`, form);
      else await api.post("/email-templates", form);
      toast.success(template?.system ? "Gespeichert – gilt ab sofort für alle neuen Termine" : "Gespeichert");
      onOpenChange(false); onSaved();
    }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white">
        <DialogHeader><DialogTitle className="font-display">{template ? "Vorlage bearbeiten" : "Neue Vorlage"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {template?.system && (
            <div className="text-xs rounded-lg bg-[#F5F6F0] border border-[#E5E7EB] text-[#2C3E2D] px-3 py-2">
              {template.usage} Änderungen werden ab dem nächsten Versand automatisch verwendet.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name *</Label><Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="template-name-input" /></div>
            <div><Label>Kategorie</Label><Input className="mt-1.5" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="template-category-input" /></div>
          </div>
          <div><Label>Betreff *</Label><Input className="mt-1.5" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="template-subject-input" /></div>
          <div>
            <Label>Text * (Platzhalter: {PLACEHOLDER_HINT})</Label>
            <Textarea className="mt-1.5" rows={10} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="template-body-input" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button className="bg-[#6B7F3E] hover:bg-[#5A6B34]" onClick={save} disabled={saving} data-testid="template-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Speichern"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SendDialog({ open, template, customers, onOpenChange }) {
  const [customerId, setCustomerId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [placeholders, setPlaceholders] = useState({ name: "", termin: "", leistung: "" });
  const [sending, setSendingBusy] = useState(false);
  const customer = customers.find((c) => c.id === customerId);
  const to = customer?.email || "";

  useEffect(() => {
    if (!open || !template) return;
    setCustomerId(""); setPlaceholders({ name: "", termin: "", leistung: "" });
    setSubject(template.subject); setBody(template.body);
  }, [open, template]);

  useEffect(() => {
    if (!template) return;
    let s = template.subject; let b = template.body;
    if (customer) {
      // Standard-Anrede der Vorlagen durch Herr/Frau-Anrede ersetzen
      const greeting = greetingFor(customer);
      b = b.replace(/Sehr geehrte\/r \{name\},/g, greeting).replace(/\{anrede\}/g, greeting);
    }
    for (const k of ["name", "termin", "leistung"]) {
      const val = placeholders[k];
      const re = new RegExp(`\\{${k}\\}`, "g");
      s = s.replace(re, val || `{${k}}`);
      b = b.replace(re, val || `{${k}}`);
    }
    setSubject(s); setBody(b);
  }, [placeholders, template, customer]);

  const send = async () => {
    if (!customer || !subject) { toast.error("Empfänger und Betreff erforderlich"); return; }
    if (!to) {
      toast.error(`Für ${customer.name} ist keine E-Mail-Adresse hinterlegt. Bitte im Kundenstamm ergänzen.`);
      return;
    }
    setSendingBusy(true);
    try {
      const r = await api.post("/email/send", { to, subject, body });
      if (r.data.status === "error") {
        toast.error("E-Mail-Versand über Microsoft Graph fehlgeschlagen");
        return;
      }
      if (r.data.status === "simulated") {
        toast.warning("E-Mail NICHT gesendet – Microsoft Graph nicht konfiguriert");
      } else {
        toast.success("E-Mail gesendet");
      }
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingBusy(false); }
  };

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(`Betreff: ${subject}\n\n${body}`);
    toast.success("In Zwischenablage kopiert");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader><DialogTitle className="font-display">Vorlage verwenden</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Empfänger</Label>
            <Select
              value={customerId}
              onValueChange={(v) => {
                setCustomerId(v);
                const c = customers.find((x) => x.id === v);
                if (c) setPlaceholders((p) => ({ ...p, name: contactName(c) || c.name }));
              }}
            >
              <SelectTrigger className="mt-1.5" data-testid="send-to-select">
                <SelectValue placeholder="Kunden auswählen" />
              </SelectTrigger>
              <SelectContent>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name} — {c.email || "keine E-Mail hinterlegt"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {["name", "termin", "leistung"].map((k) => (
              <div key={k}>
                <Label className="text-xs capitalize">{k}</Label>
                <Input
                  className="mt-1" placeholder={`{${k}}`}
                  value={placeholders[k]}
                  onChange={(e) => setPlaceholders({ ...placeholders, [k]: e.target.value })}
                  data-testid={`send-placeholder-${k}`}
                />
              </div>
            ))}
          </div>
          <div><Label>Betreff</Label><Input className="mt-1.5" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="send-subject-input" /></div>
          <div><Label>Nachricht</Label><Textarea rows={8} className="mt-1.5" value={body} onChange={(e) => setBody(e.target.value)} data-testid="send-body-input" /></div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={copyToClipboard} data-testid="send-copy-btn">
            <Copy className="w-3.5 h-3.5 mr-1" /> Kopieren
          </Button>
          <Button className="bg-[#6B7F3E] hover:bg-[#5A6B34]" onClick={send} disabled={sending} data-testid="send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-1" /> Senden</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
